import { forwardRef, TextareaHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const textareaVariants = cva(
  'flex w-full rounded-md border bg-background px-3 py-3 text-sm text-foreground placeholder:text-muted-foreground resize-vertical focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-colors shadow-input sm:py-2',
  {
    variants: {
      variant: {
        default: 'border-input focus:ring-2 focus:ring-ring focus:border-transparent',
        error: 'border-destructive focus:ring-2 focus:ring-destructive focus:border-transparent',
      },
      size: {
        default: 'min-h-[88px] sm:min-h-[80px]', // Larger minimum height on mobile
        sm: 'min-h-[68px] text-xs sm:min-h-[60px]', // Larger minimum height on mobile
        lg: 'min-h-[128px] sm:min-h-[120px]', // Larger minimum height on mobile
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  error?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, variant, size, ...props }, ref) => {
    return (
      <div className="w-full">
        <textarea
          className={cn(
            textareaVariants({
              variant: error ? 'error' : variant,
              size,
              className,
            })
          )}
          ref={ref}
          {...props}
        />
        {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
export { Textarea };
